import { useEffect } from 'react'
import { sbDrag, nearNoteHandle } from '../sidebar/sbDrag'
import { selectionHeld } from '../agents/selectionHold'
import { FLING_SAMPLE_MS, flingStep, flingVelocity } from './fling'
import type { TerminalSocket } from '../transport/ws'

/**
 * The terminal's touch gestures, ported from the original (index.html ~12690).
 *
 * One handler, axis-locked, covering three gestures so they can't fight:
 *
 *   vertical drag   → scroll scrollback / tmux history, via a SYNTHETIC WHEEL
 *   horizontal drag → follow-the-thumb sidebar (open when closed, reverse to back off)
 *   hold still      → long-press hook (the original opens a select & copy popup)
 *
 * Axis locks once travel passes TOUCH_THRESH, so scrolling and the sidebar never both
 * claim the same finger. A plain tap stays a tap. Pinch (2+ touches) is ignored outright.
 *
 * The vertical branch calls markScrolled() — a DELIBERATE gesture, which is the only
 * sanctioned caller (see the warning on markScrolled: never wire it to xterm's onScroll).
 */

/*
 * FINGER PX → WHEEL PX, and it is 1:1 on purpose.
 *
 * It was 0.5, which is measurable and was measured: a 300px drag scrolled 150px. The
 * content moved at half the thumb, so it never sat under the finger — every other
 * scrollable surface on the phone tracks 1:1, and anything less reads as the terminal
 * lagging behind you rather than as "a slower scroll". Owner, 2026-08-22: 手機窄螢幕上
 * 使用手指滑動 Terminal 非常的不順.
 */
const TOUCH_SPEED = 1
const TOUCH_THRESH = 6 // px of travel before a touch becomes a gesture
const LONGPRESS_MS = 450

/**
 * Scroll by dispatching a wheel event at the terminal's CENTRE.
 *
 * The coordinates are not incidental. Full-screen TUIs that enable mouse reporting
 * (cursor-agent, tmux `mouse on`, `less --mouse`) receive the wheel as a POSITIONED mouse
 * report and decide what to scroll from that position. A default (0,0) lands in a corner —
 * a status line or header — which some TUIs ignore outright, so history simply wouldn't
 * move. Centring puts the event over the scrollable content.
 */
/*
 * Internal: called at line ~119 of this file, every frame of a touch scroll.
 *
 * It carried an `export` and an entry on the dead-export allowlist whose stated reason —
 * "unused since the drawer rewrite" — was FALSE. It only looked unused because that scan
 * required a reference from a DIFFERENT file. Un-exported 2026-09-02 so the name and the
 * truth agree.
 */
function wheelScroll(deltaY: number, root: HTMLElement | null, at?: { x: number; y: number }) {
  const target = (root?.querySelector('.xterm-viewport') as HTMLElement | null) ?? root
  if (!target) return
  /*
   * The centre, measured ONCE per gesture by the caller when it can. Reading
   * getBoundingClientRect here costs a layout on every dispatch, and a dispatch can
   * happen on every touchmove — 120 times a second on a modern phone, in the middle
   * of the gesture we are trying to make smooth. The box does not move mid-swipe.
   */
  const c = at ?? (() => {
    const r = target.getBoundingClientRect()
    return { x: Math.round(r.left + r.width / 2), y: Math.round(r.top + r.height / 2) }
  })()
  target.dispatchEvent(
    new WheelEvent('wheel', {
      deltaY,
      deltaMode: 0,
      bubbles: true,
      cancelable: true,
      clientX: c.x,
      clientY: c.y,
    }),
  )
}

export function useTermGestures(
  host: { current: HTMLElement | null },
  socketRef: { current: TerminalSocket | null },
  onLongPress?: () => void,
) {
  useEffect(() => {
    const el = host.current
    if (!el) return

    let active = false
    let mode: null | 'v' | 'h' = null
    let startX = 0
    let startY = 0
    let lastY = 0
    let lp: number | null = null
    /** The viewport's centre, measured once per gesture — see wheelScroll. */
    let centre: { x: number; y: number } | undefined
    /** Deltas that arrived this frame, waiting for one dispatch. */
    let pending = 0
    let raf: number | null = null
    /** Recent finger positions, for the velocity a flick leaves behind. */
    let track: { t: number; y: number }[] = []
    let glide: number | null = null

    const centreOf = (): { x: number; y: number } | undefined => {
      const v = el.querySelector('.xterm-viewport') as HTMLElement | null
      if (!v) return undefined
      const r = v.getBoundingClientRect()
      return { x: Math.round(r.left + r.width / 2), y: Math.round(r.top + r.height / 2) }
    }

    const stopGlide = () => {
      if (glide !== null) window.cancelAnimationFrame(glide)
      glide = null
    }

    /*
     * ONE DISPATCH PER FRAME.
     *
     * touchmove fires as fast as the screen samples — 120Hz on a current phone — and
     * every dispatch used to build a WheelEvent and read the viewport's box. Nothing
     * downstream can act more than once per frame anyway, so the extra ones were work
     * done inside the gesture we are trying to make smooth. Deltas add up; none is lost.
     */
    const flush = () => {
      raf = null
      if (pending === 0) return
      const d = pending
      pending = 0
      wheelScroll(d, el, centre)
    }
    const push = (delta: number) => {
      pending += delta
      if (raf === null) raf = window.requestAnimationFrame(flush)
    }

    /*
     * DESKTOP WHEEL IS DELIBERATELY LEFT ALONE (reverted coalescer, 2026-08-28).
     * A per-frame wheel coalescer shipped here briefly against the Mac scroll
     * tearing, on the theory that the momentum event flood reached tmux as a
     * report flood. It does not: with mouse tracking active, xterm emits AT
     * MOST ONE positioned report per wheel EVENT (browser/Terminal.ts — the
     * scrolled amount is only sign-checked, triggerMouseEvent is a single
     * call), and sub-line pixel deltas accumulate in _wheelPartialScroll until
     * a line boundary is crossed. So the raw path's report rate already tracks
     * lines-scrolled, not events; summing a frame's deltas into one synthetic
     * event just ATE the accumulated lines (one report per frame regardless of
     * size) and made scrolling several times slower while the tearing stayed.
     * The tear lives in another layer — do not re-intercept wheel here without
     * wire measurements first.
     */

    const clearLP = () => {
      if (lp !== null) {
        window.clearTimeout(lp)
        lp = null
      }
    }

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        // Pinch — leave it alone entirely.
        active = false
        clearLP()
        return
      }
      const t = e.touches[0]!
      // A finger down is a brake: whatever the last flick was still doing, stop.
      stopGlide()
      active = true
      mode = null
      startX = t.clientX
      startY = lastY = t.clientY
      centre = centreOf()
      track = [{ t: e.timeStamp || performance.now(), y: t.clientY }]
      clearLP()
      if (onLongPress) {
        lp = window.setTimeout(() => {
          lp = null
          onLongPress()
        }, LONGPRESS_MS)
      }
    }

    const onMove = (e: TouchEvent) => {
      if (!active || e.touches.length !== 1) return
      const t = e.touches[0]!
      const x = t.clientX
      const y = t.clientY
      const dx = x - startX
      const dy = y - startY

      if (!mode) {
        if (Math.abs(dx) < TOUCH_THRESH && Math.abs(dy) < TOUCH_THRESH) return // still a tap
        mode = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v'
        clearLP() // the finger moved — not a long-press
        if (mode === 'v') {
          // Scrolling may put tmux into copy mode, where keystrokes are eaten — the next
          // input must cancel it first. Deliberate gesture: the sanctioned caller.
          socketRef.current?.markScrolled()
        }
      }

      if (mode === 'v') {
        const step = y - lastY
        lastY = y
        // Natural direction: drag DOWN reveals older lines (i.e. scroll up).
        push(-step * TOUCH_SPEED)
        const now = e.timeStamp || performance.now()
        track.push({ t: now, y })
        // Only the tail decides the flick, so only the tail is kept.
        while (track.length > 2 && now - track[0]!.t > FLING_SAMPLE_MS) track.shift()
        e.preventDefault()
        return
      }

      // Horizontal: the rail tracks the thumb. No latch — sbDrag is a pure function of
      // finger X, so reversing mid-drag backs it off.
      if (!sbDrag.isActive()) sbDrag.begin(startX, nearNoteHandle(startX, startY))
      sbDrag.move(x)
      e.preventDefault() // keep browser edge-nav / selection out of it
    }

    const onEnd = () => {
      clearLP()
      /*
       * THE FLICK CARRIES ON. Measured before this existed: a 300px drag produced
       * 150px of scroll and exactly 0 after the lift — the scrollback stopped dead
       * under the finger, which is not how any other surface on the phone behaves.
       *
       * Velocity comes from the last ~90ms only, so swiping hard and then holding
       * still before letting go still stops where you put it. That press-and-hold is
       * the only brake a touch surface has, and it must beat the momentum.
       */
      const wasScrolling = mode === 'v'
      active = false
      mode = null
      if (sbDrag.isActive()) sbDrag.end()
      if (!wasScrolling) return
      let v = flingVelocity(track)
      track = []
      if (v === 0) return
      let last = performance.now()
      const frame = (now: number) => {
        glide = null
        const { delta, next } = flingStep(v, now - last)
        last = now
        v = next
        if (delta === 0) return
        push(-delta * TOUCH_SPEED)
        if (v !== 0) glide = window.requestAnimationFrame(frame)
      }
      glide = window.requestAnimationFrame(frame)
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd)
    el.addEventListener('touchcancel', onEnd)

    return () => {
      clearLP()
      stopGlide()
      if (raf !== null) window.cancelAnimationFrame(raf)
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [host, socketRef, onLongPress])
}

/**
 * The horizontal half only, for surfaces that sit ABOVE #term (transcript, project chat,
 * DM). Without this the rail can be opened by dragging the terminal but not from any of
 * those views — and closing it would be impossible while one is open.
 *
 * Axis-locked so their own vertical scrolling is untouched, and it declines to hijack the
 * resize handle or text fields.
 */
export function useThumbDrag(ref: { current: HTMLElement | null }, enabled = true) {
  useEffect(() => {
    const el = ref.current
    if (!el || !enabled) return

    const AXIS_TH = 6
    let on = false
    let mode: null | 'h' | 'v' = null
    let x0 = 0
    let y0 = 0

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        on = false
        return
      }
      const tgt = e.target as HTMLElement | null
      if (tgt?.closest('#sbResize, input, textarea, select, [contenteditable="true"]')) {
        on = false // don't fight resize-drag or text selection
        return
      }
      // A drag that starts inside a table scroller is CONTENT PANNING — the whole
      // reason the wrapper exists (owner: 水平拖動是瀏覽到更多內容的要素). Claiming
      // it for the rail would make wide tables unreadable exactly where they
      // finally became readable.
      //
      // And the markdown CODE BLOCK, the table's sibling that missed both halves
      // of the cure: it gained touch-action: pan-x pan-y (so iOS lets it scroll)
      // and the very next report was the rail fighting the finger for the same
      // drag (owner, 2026-08-26: 滑動時與 side panel 的手勢打架了). Same rule,
      // same reason: a pan that starts inside a horizontal scroller belongs to
      // the content.
      if (tgt?.closest('.md-tablewrap, .pmsg-body.md pre')) {
        on = false
        return
      }
      /*
       * TEXT IS BEING SELECTED — the gesture is theirs, not the rail's.
       *
       * owner, 2026-08-10: Clean View 在 select 狀態準備要複製文字的狀態下，解除
       * 從右到左的 side panel 滑動手勢偵測，這會干擾文字的選擇.
       *
       * Once a selection exists, the next horizontal drag is almost always a
       * SELECTION HANDLE being widened — and this hook answers it by calling
       * preventDefault() in onMove, which is precisely what stops iOS moving the
       * handle. The two gestures are identical to a listener (one finger, sideways)
       * and only the selection tells them apart, so the selection is what decides.
       *
       * Same predicate the transcript already freezes on (selectionHeld): a
       * COLLAPSED selection is a caret, which is just a tap and must not disable the
       * rail; and a selection whose ends are both outside this element — the
       * composer, the sidebar — is not this drag's business.
       */
      if (selectionHeld(el, document.getSelection())) {
        on = false
        return
      }
      on = true
      mode = null
      x0 = e.touches[0]!.clientX
      y0 = e.touches[0]!.clientY
    }

    const onMove = (e: TouchEvent) => {
      if (!on || e.touches.length !== 1) return
      const x = e.touches[0]!.clientX
      const y = e.touches[0]!.clientY
      if (!mode) {
        if (Math.abs(x - x0) < AXIS_TH && Math.abs(y - y0) < AXIS_TH) return
        mode = Math.abs(x - x0) > Math.abs(y - y0) ? 'h' : 'v'
      }
      if (mode !== 'h') return // vertical → leave native scrolling alone
      /*
       * Re-checked here, not only at touchstart: on iOS the selection is BORN from a
       * long-press, so a finger that presses and then slides straight into widening
       * it started before there was any selection to see. Only the first claim is
       * gated — once the rail is genuinely being dragged, a selection appearing
       * mid-gesture must not strand it half-open.
       */
      if (!sbDrag.isActive() && selectionHeld(el, document.getSelection())) {
        on = false
        return
      }
      if (!sbDrag.isActive()) sbDrag.begin(x0, nearNoteHandle(x0, y0))
      sbDrag.move(x)
      e.preventDefault()
    }

    const done = () => {
      on = false
      mode = null
      if (sbDrag.isActive()) sbDrag.end()
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', done)
    el.addEventListener('touchcancel', done)

    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', done)
      el.removeEventListener('touchcancel', done)
    }
  }, [ref, enabled])
}
